import { effect, Injectable, signal } from '@angular/core';
import { AuthStore } from '@app/core/state/auth.store';

import { ClientEntity } from '../models/entity-client-store.model';
import { SupplierEntityStore } from '../models/entity-supplier-store.model';
import { LoggingService } from '../services';

@Injectable({ providedIn: 'root' })
export class EntityStore {
    constructor(
        private loggingService: LoggingService,
        private authStore: AuthStore
    ) {
        // Log whenever selectedSupplier changes
        effect(() => {
            const sup = this._selectedSupplier();
            if (sup) {
                this.loggingService.debug('EntityStore: selectedSupplier updated', sup);
            } else {
                this.loggingService.debug('EntityStore: selectedSupplier cleared');
            }
        });

        // Log whenever selectedClient changes
        effect(() => {
            const cli = this._selectedClient();
            if (cli) {
                this.loggingService.debug('EntityStore: selectedClient updated', cli);
            } else {
                this.loggingService.debug('EntityStore: selectedClient cleared');
            }
        });
    }

    // --- Suppliers ---
    private _suppliers = signal<SupplierEntityStore[]>([]);
    public readonly suppliers = this._suppliers.asReadonly();

    private _selectedSupplier = signal<SupplierEntityStore | null>(null);
    public readonly selectedSupplier = this._selectedSupplier.asReadonly();

    // --- Clients ---
    private _clients = signal<ClientEntity[]>([]);
    public readonly clients = this._clients.asReadonly();

    private _selectedClient = signal<ClientEntity | null>(null);
    public readonly selectedClient = this._selectedClient.asReadonly();

    // ====== Suppliers API ======
    getSuppliers(): SupplierEntityStore[] {
        return this._suppliers();
    }
    setSuppliers(list: SupplierEntityStore[]): void {
        this._suppliers.set(list);
    }
    getSelectedSupplier(): SupplierEntityStore | null {
        return this._selectedSupplier();
    }
    setSelectedSupplier(item: SupplierEntityStore | null): void {
        this._selectedSupplier.set(item);
    }

    // ====== Clients API ======
    getClients(): ClientEntity[] {
        return this._clients();
    }
    setClients(list: ClientEntity[]): void {
        this._clients.set(list);
    }
    getSelectedClient(): ClientEntity | null {
        return this._selectedClient();
    }
    setSelectedClient(item: ClientEntity | null): void {
        this._selectedClient.set(item);
    }

    /** Remove tenant-scoped entities when the authenticated session ends. */
    clearSessionState(): void {
        this._suppliers.set([]);
        this._selectedSupplier.set(null);
        this._clients.set([]);
        this._selectedClient.set(null);
    }

    /**
     * Initialize the selected client and supplier from user/session data.
     * Call this after login/session restore.
     * Pass in the user/session payload from authService.
     */
    initializeFromAuth(user: any): void {
        this.loggingService.debug('[DEBUG][EntityStore] initializeFromAuth called. User:', user);

        // --- Seed clients from authStore if not already set ---
        const existingClients = this.getClients();
        if (!existingClients || existingClients.length === 0) {
            const ui = this.authStore.userInfoData();
            if (ui?.clientId) {
                const seededClient: ClientEntity = {
                    clientId: ui.clientId,
                    clientName: ui.clientName ?? '',
                    // add other ClientEntity fields if required
                } as ClientEntity;
                this.setClients([seededClient]);
                this.loggingService.debug('[EntityStore] Clients seeded from authStore', [seededClient]);
            }
        }

        // --- Handle client selection ---
        if (user && user.clientId) {
            const match = this.getClients().find(c => c.clientId === user.clientId) || null;
            this.setSelectedClient(match);
            this.loggingService.debug('[EntityStore] Selected client initialized from auth', match);
        } else {
            this.setSelectedClient(null);
            this.loggingService.debug('[EntityStore] No clientId found in auth; selection cleared');
        }

        // --- Handle supplier (if needed) ---
        if (user && user.supplierId) {
            const supplier = this.getSuppliers().find(s => s.supplierId === user.supplierId) || null;
            this.setSelectedSupplier(supplier);
            this.loggingService.debug('[EntityStore] Selected supplier initialized from auth', supplier);
        } else {
            this.setSelectedSupplier(null);
            this.loggingService.debug('[EntityStore] No supplierId found in auth; selection cleared');
        }
    }
}
